import { useState, useEffect, useCallback } from "react";
import { useAuth, getRoleLabel, getRoleColor, hasPermission, UserRole } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/api";
import { Shield, MapPin, Mail, ChevronDown, Plus, Pencil, Trash2, X, Check, Phone, Key, RefreshCw, Copy } from "lucide-react";
import "./Team.css";

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: UserRole;
  storeLocation: string;
  status: "active" | "inactive";
  pin?: string;
  storeId?: string;
}

function generatePin(): string {
  return String(Math.floor(1000 + Math.random() * 9000));
}

export default function Team() {
  const { user } = useAuth();
  const canManage = hasPermission(user?.role, "team:manage");
  const isRegisteredStore = !!user?.storeId;

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", role: "salesperson" as UserRole, pin: "" });
  const [formError, setFormError] = useState("");
  const [copiedPin, setCopiedPin] = useState(false);

  const storeLocation = user?.storeLocation || "Store";

  const fetchTeam = useCallback(async () => {
    if (!isRegisteredStore) {
      setMembers([]);
      setLoading(false);
      return;
    }
    try {
      const data = await apiRequest<TeamMember[]>(`/stores/${user.storeId}/team`);
      setMembers(data);
    } catch {
      setMembers([]);
    }
    setLoading(false);
  }, [isRegisteredStore, user?.storeId]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  if (!canManage) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Team Management</h1>
        </div>
        <div className="card">
          <div className="empty-state">
            <Shield size={40} style={{ marginBottom: 12, color: "var(--danger)" }} />
            <h3>Access Restricted</h3>
            <p>Only administrators can manage team members.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleRoleChange = async (id: string, newRole: UserRole) => {
    if (isRegisteredStore) {
      try {
        await apiRequest(`/stores/${user?.storeId}/team/${id}`, {
          method: "PUT",
          body: JSON.stringify({ role: newRole }),
        });
        setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, role: newRole } : m)));
      } catch {}
    } else {
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, role: newRole } : m)));
    }
    setEditingId(null);
  };

  const openAddModal = () => {
    setEditForm({ name: "", email: "", phone: "", role: "salesperson", pin: generatePin() });
    setFormError("");
    setCopiedPin(false);
    setShowAddModal(true);
  };

  const openEditModal = (member: TeamMember) => {
    setEditForm({ name: member.name, email: member.email, phone: member.phone || "", role: member.role, pin: member.pin || "" });
    setFormError("");
    setCopiedPin(false);
    setEditingId(member.id);
    setShowEditModal(true);
  };

  const validateForm = (): boolean => {
    if (!editForm.name.trim()) { setFormError("Name is required"); return false; }
    if (!editForm.email.trim()) { setFormError("Email is required"); return false; }
    if (!/\S+@\S+\.\S+/.test(editForm.email)) { setFormError("Enter a valid email"); return false; }
    if (editForm.pin && !/^\d{4}$/.test(editForm.pin)) { setFormError("PIN must be exactly 4 digits"); return false; }
    if (editForm.pin) {
      const pinConflict = members.find((m) => m.pin === editForm.pin && m.id !== editingId);
      if (pinConflict) { setFormError(`PIN ${editForm.pin} is already assigned to ${pinConflict.name}`); return false; }
    }
    return true;
  };

  const handleAddMember = async () => {
    if (!validateForm()) return;
    const existing = members.find((m) => m.email.toLowerCase() === editForm.email.toLowerCase());
    if (existing) { setFormError("A team member with this email already exists"); return; }

    if (isRegisteredStore) {
      try {
        const newMember = await apiRequest<TeamMember>(`/stores/${user?.storeId}/team`, {
          method: "POST",
          body: JSON.stringify({
            name: editForm.name.trim(),
            email: editForm.email.trim(),
            phone: editForm.phone.trim(),
            role: editForm.role,
            pin: editForm.pin || undefined,
          }),
        });
        setMembers((prev) => [...prev, newMember]);
        setShowAddModal(false);
      } catch (err: any) {
        setFormError(err.message || "Failed to add member");
      }
    } else {
      const newMember: TeamMember = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
        role: editForm.role,
        storeLocation,
        status: "active",
        pin: editForm.pin || undefined,
      };
      setMembers((prev) => [...prev, newMember]);
      setShowAddModal(false);
    }
  };

  const handleEditMember = async () => {
    if (!validateForm()) return;
    if (!editingId) return;
    const existing = members.find((m) => m.email.toLowerCase() === editForm.email.toLowerCase() && m.id !== editingId);
    if (existing) { setFormError("Another team member already has this email"); return; }

    if (isRegisteredStore) {
      try {
        const updated = await apiRequest<TeamMember>(`/stores/${user?.storeId}/team/${editingId}`, {
          method: "PUT",
          body: JSON.stringify({
            name: editForm.name.trim(),
            email: editForm.email.trim(),
            phone: editForm.phone.trim(),
            role: editForm.role,
            pin: editForm.pin || undefined,
          }),
        });
        setMembers((prev) => prev.map((m) => m.id === editingId ? updated : m));
      } catch (err: any) {
        setFormError(err.message || "Failed to update member");
        return;
      }
    } else {
      setMembers((prev) =>
        prev.map((m) =>
          m.id === editingId
            ? { ...m, name: editForm.name.trim(), email: editForm.email.trim(), phone: editForm.phone.trim(), role: editForm.role, pin: editForm.pin || undefined }
            : m
        )
      );
    }
    setShowEditModal(false);
    setEditingId(null);
  };

  const handleDeleteMember = async (id: string) => {
    if (isRegisteredStore) {
      try {
        await apiRequest(`/stores/${user?.storeId}/team/${id}`, { method: "DELETE" });
      } catch {}
    }
    setMembers((prev) => prev.filter((m) => m.id !== id));
    setConfirmDeleteId(null);
  };

  const toggleStatus = async (id: string) => {
    const member = members.find((m) => m.id === id);
    if (!member) return;
    const newStatus = member.status === "active" ? "inactive" : "active";
    if (isRegisteredStore) {
      try {
        await apiRequest(`/stores/${user?.storeId}/team/${id}`, {
          method: "PUT",
          body: JSON.stringify({ status: newStatus }),
        });
      } catch {}
    }
    setMembers((prev) =>
      prev.map((m) => m.id === id ? { ...m, status: newStatus as "active" | "inactive" } : m)
    );
  };

  const copyPin = (pin: string) => {
    navigator.clipboard.writeText(pin).catch(() => {});
    setCopiedPin(true);
    setTimeout(() => setCopiedPin(false), 2000);
  };

  const roleOptions: UserRole[] = ["admin", "manager", "salesperson"];

  const roleCounts = {
    admin: members.filter((m) => m.role === "admin").length,
    manager: members.filter((m) => m.role === "manager").length,
    salesperson: members.filter((m) => m.role === "salesperson").length,
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Team Management</h1></div>
        <div className="card"><p style={{ padding: 20, textAlign: "center" }}>Loading team...</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-row">
          <div>
            <h1>Team Management</h1>
            <p>{members.length} team member{members.length !== 1 ? "s" : ""} &middot; {storeLocation}</p>
          </div>
          <button className="btn btn-primary" onClick={openAddModal}>
            <Plus size={16} /> Add Team Member
          </button>
        </div>
      </div>

      <div className="team-stats-row">
        {(["admin", "manager", "salesperson"] as UserRole[]).map((role) => (
          <div className="card team-stat-card" key={role}>
            <div className="team-stat-icon" style={{ background: `${getRoleColor(role)}15`, color: getRoleColor(role) }}>
              <Shield size={18} />
            </div>
            <div className="team-stat-value">{roleCounts[role]}</div>
            <div className="team-stat-label">{getRoleLabel(role)}s</div>
          </div>
        ))}
      </div>

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Team Member</th>
              <th>Email</th>
              <th>Role</th>
              <th>PIN</th>
              <th>Status</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className={m.status === "inactive" ? "team-row-inactive" : ""}>
                <td>
                  <div className="customer-cell">
                    <div className="avatar-sm" style={{ background: getRoleColor(m.role) }}>
                      {m.name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div>
                      <strong>{m.name}</strong>
                      {m.id === user?.id && <span className="you-badge">You</span>}
                      {m.phone && <div className="team-phone"><Phone size={11} /> {m.phone}</div>}
                    </div>
                  </div>
                </td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Mail size={14} className="text-muted" />
                    <span>{m.email}</span>
                  </div>
                </td>
                <td>
                  <div className="role-selector-wrapper">
                    {editingId === m.id && !showEditModal ? (
                      <div className="role-dropdown">
                        {roleOptions.map((r) => (
                          <button key={r} className={`role-option ${m.role === r ? "active" : ""}`}
                            onClick={() => handleRoleChange(m.id, r)}
                            style={{ borderLeft: `3px solid ${getRoleColor(r)}` }}>
                            {getRoleLabel(r)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <button className="role-badge-btn"
                        style={{ background: `${getRoleColor(m.role)}15`, color: getRoleColor(m.role), border: `1px solid ${getRoleColor(m.role)}30` }}
                        onClick={() => m.id !== user?.id && setEditingId(m.id)}
                        title={m.id === user?.id ? "Cannot change your own role" : "Click to change role"}>
                        {getRoleLabel(m.role)}
                        {m.id !== user?.id && <ChevronDown size={12} />}
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  {m.pin ? (
                    <span className="team-pin-display">
                      <Key size={12} /> {m.pin}
                    </span>
                  ) : (
                    <span className="team-no-pin">--</span>
                  )}
                </td>
                <td>
                  <button
                    className={`status-badge status-${m.status === "active" ? "active" : "stalled"} team-status-btn`}
                    onClick={() => m.id !== user?.id && toggleStatus(m.id)}
                    title={m.id === user?.id ? "Cannot deactivate yourself" : `Click to ${m.status === "active" ? "deactivate" : "activate"}`}
                  >
                    {m.status}
                  </button>
                </td>
                <td>
                  {m.id !== user?.id && (
                    <div className="team-actions">
                      <button className="team-action-btn team-edit-btn" onClick={() => openEditModal(m)} title="Edit member">
                        <Pencil size={14} />
                      </button>
                      {confirmDeleteId === m.id ? (
                        <div className="team-confirm-delete">
                          <button className="team-action-btn team-delete-confirm-btn" onClick={() => handleDeleteMember(m.id)} title="Confirm delete">
                            <Check size={14} />
                          </button>
                          <button className="team-action-btn team-cancel-btn" onClick={() => setConfirmDeleteId(null)} title="Cancel">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button className="team-action-btn team-delete-btn" onClick={() => setConfirmDeleteId(m.id)} title="Delete member">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(showAddModal || showEditModal) && (
        <div className="team-modal-overlay" onClick={() => { setShowAddModal(false); setShowEditModal(false); setEditingId(null); }}>
          <div className="team-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{showAddModal ? "Add Team Member" : "Edit Team Member"}</h3>

            {formError && <div className="team-form-error">{formError}</div>}

            <label>Full Name</label>
            <input
              type="text"
              placeholder="Enter full name"
              value={editForm.name}
              onChange={(e) => { setEditForm({ ...editForm, name: e.target.value }); setFormError(""); }}
              autoFocus
            />

            <label>Email</label>
            <input
              type="email"
              placeholder="name@store.com"
              value={editForm.email}
              onChange={(e) => { setEditForm({ ...editForm, email: e.target.value }); setFormError(""); }}
            />

            <label>Phone</label>
            <input
              type="text"
              placeholder="(555) 123-4567"
              value={editForm.phone}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
            />

            <label>Quick Login PIN (4 digits)</label>
            <div className="team-pin-input-row">
              <input
                type="text"
                placeholder="4-digit PIN"
                value={editForm.pin}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                  setEditForm({ ...editForm, pin: val });
                  setFormError("");
                }}
                maxLength={4}
                className="team-pin-input"
              />
              <button type="button" className="team-pin-generate" onClick={() => setEditForm({ ...editForm, pin: generatePin() })} title="Generate random PIN">
                <RefreshCw size={14} /> Generate
              </button>
              {editForm.pin && editForm.pin.length === 4 && (
                <button type="button" className="team-pin-copy" onClick={() => copyPin(editForm.pin)} title="Copy PIN">
                  <Copy size={14} /> {copiedPin ? "Copied!" : "Copy"}
                </button>
              )}
            </div>

            <label>Role</label>
            <div className="team-role-picker">
              {roleOptions.map((r) => (
                <button
                  key={r}
                  className={`team-role-chip ${editForm.role === r ? "team-role-chip-active" : ""}`}
                  style={editForm.role === r ? { background: getRoleColor(r), color: "white", borderColor: getRoleColor(r) } : { borderColor: getRoleColor(r), color: getRoleColor(r) }}
                  onClick={() => setEditForm({ ...editForm, role: r })}
                >
                  {getRoleLabel(r)}
                </button>
              ))}
            </div>

            <div className="team-store-location-display">
              <MapPin size={14} />
              <span>Store Location: <strong>{storeLocation}</strong></span>
            </div>

            <div className="team-modal-actions">
              <button className="team-modal-cancel" onClick={() => { setShowAddModal(false); setShowEditModal(false); setEditingId(null); }}>Cancel</button>
              <button className="team-modal-submit" onClick={showAddModal ? handleAddMember : handleEditMember}>
                {showAddModal ? "Add Member" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
